import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { favoritesService } from '@/services/favorites';
import { generateRandomNickname, generateReferralCode } from '@/components/common/TwoSyllableWords';

interface User {
  id: string;
  email?: string;
  name?: string;
  role: 'buyer' | 'seller' | 'admin';
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  hasSession: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, name: string, role?: 'buyer' | 'seller', phone?: string) => Promise<{ success: boolean; error?: string; user?: User }>;
  signOut: () => Promise<void>;
  retryAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSession, setHasSession] = useState(false);

  // 로컬 캐시 유틸리티 함수들
  const setCachedUser = (userData: User | null) => {
    try {
      if (userData) {
        localStorage.setItem('ecodeal_user_cache', JSON.stringify(userData));
      } else {
        localStorage.removeItem('ecodeal_user_cache');
      }
    } catch (error) {
      console.warn('[Auth] Failed to cache user data:', error);
    }
  };

  const getCachedUser = (): User | null => {
    try {
      const cached = localStorage.getItem('ecodeal_user_cache');
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.warn('[Auth] Failed to get cached user data:', error);
      return null;
    }
  };

  // 강제 재시도 함수
  const retryAuth = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    
    try {
      // 1. 기존 세션 강제 재확인
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        throw new Error(`세션 확인 실패: ${error.message}`);
      }
      
      if (session?.user) {
        // 2. 세션이 있으면 사용자 데이터 다시 가져오기
        const userData = await fetchUserDataWithTimeout(session.user.id, 3000);
        if (userData) {
          setUser(userData);
          setCachedUser(userData);
          setError(null);
          setHasSession(true);
          await performPostAuthSync(userData);
        } else {
          throw new Error('사용자 정보를 가져올 수 없습니다.');
        }
      } else {
        // 3. 세션이 없으면 로그아웃 상태로 설정
        setUser(null);
        setCachedUser(null);
        setError(null);
        setHasSession(false);
      }
    } catch (error: any) {
      console.error('[Auth] Retry failed:', error);
      setError(error.message || '인증 재시도에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 타임아웃이 적용된 사용자 데이터 조회
  const fetchUserDataWithTimeout = async (userId: string, timeout = 2000): Promise<User | null> => {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Query timeout')), timeout);
      });

      const queryPromise = supabase
        .from('users')
        .select('id, email, name, role, phone')
        .eq('id', userId)
        .maybeSingle();

      const { data, error } = await Promise.race([
        queryPromise,
        timeoutPromise
      ]) as any;

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.log(`[Auth] Query failed: ${error.message}`);
      // 에러 시 null 반환 (fallback은 호출하는 곳에서 처리)
      return null;
    }
  };


  // 로그인 성공 후 추가 동기화 작업
  const performPostAuthSync = async (userData: User) => {
    // 즐겨찾기 마이그레이션
    try {
      const migrationResult = await favoritesService.migrateLocalFavoritesToSupabase(userData.id);
      if (migrationResult.success && migrationResult.migrated && migrationResult.migrated > 0) {
      }
    } catch (error) {
      console.error('[Auth] Favorites migration failed (non-critical):', error);
    }

    // 장바구니 동기화 (구매자만)
    if (userData.role === 'buyer') {
      try {
        const { useCartStore } = await import('@/store/cart');
        const { cartService } = await import('@/services/cart');
        
        // DB 상태 확인 후 syncFromDB 실행 (캐시 사용자라도 실행)
        const dbCart = await cartService.getCurrentCart(userData.id);
        if (dbCart && dbCart.items && dbCart.items.length > 0) {
          await useCartStore.getState().syncFromDB(userData.id);
        }
      } catch (error) {
        console.error('[Auth] Cart sync failed (non-critical):', error);
      }
    }
  };


  useEffect(() => {
    let isMounted = true;
    let safetyTimer: NodeJS.Timeout | null = null;
    
    // 캐시된 사용자 즉시 로드 (UI 블로킹 없음)
    const cachedUser = getCachedUser();
    if (cachedUser) {
      setUser(cachedUser);
      setIsLoading(false);
      setError(null);
    } else {
      setIsLoading(true);
      setError(null);
      
      // 🛡️ 안전장치: 8초 후 강제 로딩 상태 리셋 (캐시가 없는 경우만)
      safetyTimer = setTimeout(() => {
        if (isMounted && isLoading) {
          console.warn('[Auth] ⚠️ Safety timeout triggered - forcing reset after 8 seconds');
          setError('로그인 상태 확인이 지연되고 있습니다. 새로고침해주세요.');
          setIsLoading(false);
        }
      }, 8000);
    }

    // 백그라운드에서 실제 세션 확인 (UI 블로킹 없음)
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        
        if (error) {
          console.error('[Auth] Session check error:', error);
          setHasSession(false);
          // 캐시가 있으면 유지, 없으면 에러 표시
          if (!cachedUser) {
            setError('세션 확인 중 오류가 발생했습니다.');
            setUser(null);
            setIsLoading(false);
          }
          return;
        }
        
        if (session?.user) {
          setHasSession(true);
          // 세션이 있으면 최신 데이터 가져오기
          try {
            const userData = await fetchUserDataWithTimeout(session.user.id, 2000);
            if (userData && isMounted) {
              // 캐시된 데이터와 다르면 업데이트
              if (JSON.stringify(userData) !== JSON.stringify(cachedUser)) {
                setUser(userData);
                setCachedUser(userData);
              }
              setError(null);
              
              // 최초 로그인 시에만 추가 동기화 수행 (비동기)
              if (!cachedUser) {
                performPostAuthSync(userData).catch(error => {
                  console.error('[Auth] Post-auth sync failed (non-critical):', error);
                });
              }
            }
          } catch (error) {
            // 캐시가 있으면 유지
          }
        } else {
          // 세션이 없으면 로그아웃 상태로 전환
          setHasSession(false);
          if (cachedUser) {
            setUser(null);
            setCachedUser(null);
          }
          setError(null);
        }
        
        // 캐시가 없었다면 로딩 상태 해제
        if (!cachedUser && isMounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('[Auth] Session check exception:', error);
        if (!cachedUser && isMounted) {
          setError('세션 확인 중 오류가 발생했습니다.');
          setIsLoading(false);
        }
      }
    };
    
    // 백그라운드에서 세션 확인 실행
    checkSession();
    
    // 🔄 세션 변경 감지 - 심플한 이벤트 처리
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        
        if (!isMounted) return;
        
        try {
          if (event === 'SIGNED_IN' && session?.user) {
            // 중요: 같은 사용자가 재로그인하는 경우도 처리해야 함
            // 로그아웃 후 재로그인 시 user 상태를 반드시 업데이트
            setIsLoading(true);
            setHasSession(true);
            
            const userData = await fetchUserDataWithTimeout(session.user.id, 2000);
            if (userData && isMounted) {
              setUser(userData);
              setCachedUser(userData);
              setError(null);
              // 비동기로 실행하여 로그인 블로킹 방지
              performPostAuthSync(userData).catch(error => {
                console.error('[Auth] Post-auth sync failed (non-critical):', error);
              });
            } else if (isMounted && session?.user) {
              // fetchUserDataWithTimeout 실패 시 세션 데이터로 fallback
              const fallbackUser = {
                id: session.user.id,
                email: session.user.email || '',
                name: session.user.user_metadata?.name || '사용자',
                role: (session.user.user_metadata?.role || 'buyer') as 'buyer' | 'seller' | 'admin'
              };
              setUser(fallbackUser);
              setCachedUser(fallbackUser);
              setError(null);
            }
            
            setIsLoading(false);
          } else if (event === 'SIGNED_OUT') {
            
            // Clear cart on logout
            try {
              const { useCartStore } = await import('@/store/cart');
              useCartStore.getState().clearCart();
            } catch (error) {
              console.error('[Auth] Failed to clear cart:', error);
            }
            
            setUser(null);
            setCachedUser(null);
            setError(null);
            setHasSession(false);
            setIsLoading(false);
          } else if (event === 'TOKEN_REFRESHED') {
            setHasSession(!!session?.user);
          }
        } catch (error) {
          console.error(`[Auth] Event ${event} processing failed:`, error);
          if (isMounted) {
            setError('인증 처리 중 오류가 발생했습니다.');
            setIsLoading(false);
          }
        }
      }
    );

    return () => {
      isMounted = false;
      if (safetyTimer) clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // 먼저 일반 로그인 시도
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // 로그인 실패 시 판매자 첫 로그인인지 확인
        if (error.message.includes('Invalid login credentials')) {
          
          // seller-first-login Edge Function 호출
          const { data: firstLoginData, error: firstLoginError } = await supabase.functions.invoke(
            'seller-first-login',
            {
              body: { email, password }
            }
          );

          if (firstLoginData?.success) {
            // 비밀번호가 업데이트되었으므로 다시 로그인 시도
            const { error: retryError } = await supabase.auth.signInWithPassword({
              email,
              password
            });

            if (retryError) {
              return { success: false, error: '로그인에 실패했습니다. 다시 시도해주세요.' };
            }

            return { success: true };
          } else if (firstLoginError || !firstLoginData?.success) {
            // 판매자 첫 로그인도 아니면 원래 에러 반환
            return { success: false, error: error.message };
          }
        }
        
        return { success: false, error: error.message };
      }

      // 로그인 성공
      if (authData?.user) {
        // 판매자인 경우 승인 상태 확인
        const userRole = authData.user.user_metadata?.role || authData.user.app_metadata?.role;
        
        if (userRole === 'seller') {
          const { data: sellerData, error: sellerError } = await supabase
            .from('sellers')
            .select('status')
            .eq('user_id', authData.user.id)
            .single();
          
          if (sellerError || !sellerData) {
            // sellers 테이블에 없으면 아직 승인되지 않은 상태
            await supabase.auth.signOut();
            return { success: false, error: '판매자 승인이 완료되지 않았습니다. 관리자 승인을 기다려주세요.' };
          }
          
          if (sellerData.status !== 'approved') {
            await supabase.auth.signOut();
            return { success: false, error: '판매자 승인이 거부되었거나 대기 중입니다.' };
          }
        }
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: '로그인 중 오류가 발생했습니다.' };
    }
  };

  const signUp = async (email: string, password: string, name: string, role: 'buyer' | 'seller' = 'buyer', phone?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            full_name: name,
            role
          }
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      let authUser = data.user || null;

      if (!data.session) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError || !signInData?.user) {
          return { success: false, error: '세션을 확보하지 못했습니다. 잠시 후 다시 시도해주세요.' };
        }

        authUser = signInData.user;
      }

      // 사용자 정보 저장
      if (authUser) {
        const { error: userError } = await supabase
          .from('users')
          .insert({
            id: authUser.id,
            email,
            name,
            role,
            phone
          });

        if (userError) {
          console.error('Failed to create user record:', userError);
          // users 테이블 생성 실패는 치명적이므로 에러 반환
          return { success: false, error: 'users 테이블 생성에 실패했습니다.', user: undefined };
        }

        // Create profile based on role
        if (role === 'buyer') {
          const randomNickname = generateRandomNickname();
          const referralCode = generateReferralCode();
          
          const { error: profileError } = await supabase
            .from('buyer_profiles')
            .insert({
              user_id: authUser.id,
              nickname: randomNickname,
              referral_code: referralCode,
            });
            
          if (profileError && profileError.code !== '23505') {
            console.error('Failed to create buyer profile:', profileError);
            // 프로필 생성 실패는 치명적이므로 에러 반환
            return { success: false, error: 'buyer_profiles 생성에 실패했습니다.', user: undefined };
          }
        } else if (role === 'seller') {
          const { error: sellerError } = await supabase
            .from('sellers')
            .insert({
              user_id: authUser.id
            });
            
          if (sellerError && sellerError.code !== '23505') {
            console.error('Failed to create seller record:', sellerError);
            return { success: false, error: 'sellers 테이블 생성에 실패했습니다.', user: undefined };
          }
        }

        return { success: true, user: {
          id: authUser.id,
          email,
          name,
          role,
          phone
        }};
      }

      return { success: true, user: undefined };
    } catch (error) {
      return { success: false, error: '회원가입 중 오류가 발생했습니다.', user: undefined };
    }
  };

  const signOut = async () => {
    try {
      // Clear cart on logout to protect user privacy
      const { useCartStore } = await import('@/store/cart');
      useCartStore.getState().clearCart();
      
      // Clear cached user data
      setCachedUser(null);
      
      // Supabase 로그아웃 호출 (이것이 SIGNED_OUT 이벤트를 발생시킴)
      await supabase.auth.signOut();
      setHasSession(false);
      
      // SIGNED_OUT 이벤트가 처리될 때까지 잠시 대기
      return new Promise<void>((resolve) => {
        setTimeout(resolve, 100);
      });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      error,
      hasSession,
      signIn,
      signUp,
      signOut,
      retryAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
